import {
    type CalendarRequest,
    type FeedEntriesByUrlsRequest,
    type FeedEntriesRequest,
    type FeedRelatedRequest,
    type FiltersRequest,
    type NewsCountRequest,
    type NewsRequest,
    type SiteSearchCountRequest,
    type SiteSearchRequest,
    type SuggestionRequest
} from "./requests";
import {autoRegister, resolve} from "../../container";
import {promiseFromJsonResponse} from "../../common/utils/promises";
import type {Nullable} from "../../bootstrap/common/types";
import {SecuredRequestExecutor} from "../../module/awswaf/securedRequestExecutor";

export type PageResponse = {
    entries: ContentEntryResponse[];
    filterData: FilterDataResponse[];
    totalCount: number;
    newCount?: number;
};

export const EMPTY_PAGE_RESPONSE: PageResponse = {
    entries: [],
    filterData: [],
    totalCount: 0
};

export type FeedPageResponse = {
    entries: ContentEntryResponse[];
    totalCount: number;
};

type FilterResponse = {
    filterData: FilterDataResponse[];
};

type EntryCountResponse = {
    totalCount: number;
};

export type ContentEntryResponse = {
    title: Nullable<string>;
    description: Nullable<string>;
    headline: Nullable<string>;
    subheadline: Nullable<string>;
    newsType: Nullable<string>;
    principalKeyword: string;
    modifiedDate: number;
    url: Nullable<string>;
    imageUrl: Nullable<string>;
    imageUrls: Nullable<ImageUrlsResponse>;
    hasSpecificImage: boolean;
    imageAltText: Nullable<string>;
    topics: Nullable<TopicResponse[]>;
    source: string[];
    contentType: Nullable<string>;
    mediaType: Nullable<string>;
    lightboxImageUrl: Nullable<string>;
    fileMetadata: Nullable<FileMetadataResponse>;
    disclaimer: Nullable<DisclaimerResponse>;
    isNew: boolean;
};

export type ImageUrlsResponse = {
    "7x4"?: string;
    "4x3"?: string;
    "3x4"?: string;
}

export type TopicResponse = {
    name: string;
    url: string;
};

export type FileMetadataResponse = {
    size: Nullable<string>;
    extension: Nullable<string>;
};

export type DisclaimerResponse = {
    headline: Nullable<string>;
    content: Nullable<string>;
};

export type FilterDataResponse = {
    id: string;
    label: string;
    matchingSources: string[];
    values: string[];
};

export type SuggestionResponse = {
    values: string[];
};

export type CalendarResponse = {
    entries: Array<CalendarEntryResponse>;
    totalCount: number;
};

export type CalendarEntryResponse = {
    id: string;
    title: string;
    start: string;
    showStartTime: boolean,
    startIsDST: boolean;
    ending: string;
    showEndingTime: boolean;
    endingIsDST: boolean;
    imageUrl: string;
    imageType: string;
    imageAltText: string;
    linkType?: string;
    link?: string;
    richDescription?: string;
    description?: string;
    text?: string;
    location?: string;
    icsDownloadEnabled?: boolean;
};

@autoRegister()
export class FeedService {
    private feedEntriesUrl: string;
    private feedRelatedUrl: string;
    private feedEntriesByUrlUrls: string;
    private newsUrl: string;
    private searchUrl: string;
    private filtersUrl: string;
    private suggestionUrl: string;
    private calendarEntriesUrl: string;

    public constructor(private securedRequestExecutor: SecuredRequestExecutor = resolve(SecuredRequestExecutor)) {
        this.feedEntriesUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/entries";
        this.feedRelatedUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/related";
        this.feedEntriesByUrlUrls = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/by-urls";
        this.newsUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/news";
        this.searchUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/search";
        this.filtersUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/filters";
        this.suggestionUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/feed/suggest";
        this.calendarEntriesUrl = "/enbw-eop/eop-app-feed-aggregator/rest/stable/calendar/entries";
    }

    public async entries(request: FeedEntriesRequest): Promise<FeedPageResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.feedEntriesUrl}/${request.lang}/${request.limit}?` + new URLSearchParams([
            ...(request.keywords.map(keyword => ["keyword", keyword])),
            ...(request.sources.map(source => ["source", source])),
            ["maxDescriptionLength", request.maxDescriptionLength.toString()],
            ["offset", request.offset.toString()],
            ...(request.excludedKeywords.map(keyword => ["exclude", keyword])),
            ["lastVisit", request.lastVisitDate.toString()],
            ["archived", "false"]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async related(request: FeedRelatedRequest): Promise<FeedPageResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.feedRelatedUrl}/${request.lang}/${request.limit}?` + new URLSearchParams([
            ...(request.sources.map(source => ["source", source])),
            ["maxDescriptionLength", String(request.maxDescriptionLength)],
            ["offset", String(request.offset)],
            ["url", request.relatedContentUrl]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async entriesByUrls(request: FeedEntriesByUrlsRequest): Promise<FeedPageResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.feedEntriesByUrlUrls}?` + new URLSearchParams([
            ...(request.urls.map(url => ["url", url])),
            ["maxDescriptionLength", String(request.maxDescriptionLength)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async news(request: NewsRequest): Promise<PageResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.newsUrl}/${request.lang}/${request.limit}?` + new URLSearchParams([
            ["searchPhrase", request.searchPhrase],
            ...(request.sources.map(source => ["source", source])),
            ...(Array.from(request.filters.entries()).flatMap(entry => entry[1].map(value => [entry[0], value]))),
            ["maxDescriptionLength", String(request.maxDescriptionLength)],
            ["offset", String(request.offset)],
            ["lastVisit", String(request.lastVisitDate)],
            ["archived", String(request.archive)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async newsCount(request: NewsCountRequest): Promise<EntryCountResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.newsUrl}/${request.lang}/0?` + new URLSearchParams([
            ["searchPhrase", request.searchPhrase],
            ...(request.sources.map(source => ["source", source])),
            ...(Array.from(request.filters.entries()).flatMap(entry => entry[1].map(value => [entry[0], value]))),
            ["archived", String(request.archive)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async search(request: SiteSearchRequest): Promise<PageResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.searchUrl}/${request.lang}/${request.limit}?` + new URLSearchParams([
            ["searchPhrase", request.searchPhrase],
            ...(request.sources.map(source => ["source", source])),
            ...(Array.from(request.filters.entries()).flatMap(entry => entry[1].map(value => [entry[0], value]))),
            ["maxDescriptionLength", request.maxDescriptionLength.toString()],
            ["offset", String(request.offset)],
            ["archived", String(request.archive)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async searchCount(request: SiteSearchCountRequest): Promise<EntryCountResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.searchUrl}/${request.lang}/0?` + new URLSearchParams([
            ["searchPhrase", request.searchPhrase],
            ...(request.sources.map(source => ["source", source])),
            ...(Array.from(request.filters.entries()).flatMap(entry => entry[1].map(value => [entry[0], value]))),
            ["archived", String(request.archive)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async filters(request: FiltersRequest): Promise<FilterResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.filtersUrl}/${request.lang}?` + new URLSearchParams([
            ...(request.sources.map(source => ["source", source])),
            ["archived", String(request.archive)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async suggestions(request: SuggestionRequest): Promise<SuggestionResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.suggestionUrl}/${request.lang}/${request.limit}?` + new URLSearchParams([
            ...(request.sources.map(source => ["source", source])),
            ["searchPhrase", String(request.searchPhrase)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public async calendarEntries(request: CalendarRequest): Promise<CalendarResponse> {
        const getRequest = (): Promise<Response> => fetch(`${this.calendarEntriesUrl}/${request.lang}/${request.limit}?` + new URLSearchParams([
            ...(request.collections.map(collection => ["collection", collection])),
            ["offset", String(request.offset)]
        ]).toString());
        const response = await this.securedRequestExecutor.execute(getRequest);
        return promiseFromJsonResponse(response);
    }

    public downloadCalendarEntryLink(id: string, language: string): string {
        return `${this.calendarEntriesUrl}/${id}/ics/${language}`;
    }
}